import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { useToast } from "../../contexts/ToastContext";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";
import capitalizeFirst from "../../utils/capitalizeFirst";
import { renderMentionText } from "../../utils/renderMentionText";
import PostComments from "../profile/PostComments";
import ConfirmModal from "../common/ConfirmModal";
import EditPost from "../profile/EditPost";
import ReportModal from "../moderation/ReportModal";
import SharePostModal from "../profile/SharePostModal";
import {
  FaHeart,
  FaRegHeart,
  FaComment,
  FaShare,
  FaTag,
  FaTimes,
  FaEdit,
  FaBookmark,
  FaRegBookmark,
  FaChevronLeft,
  FaChevronRight,
  FaEllipsisV,
  FaFlag,
} from "react-icons/fa";
import styles from "../../styles/components/product/ProductDetailsPostCard.module.css";

const ProductDetailsPostCard = ({
  post,
  onDelete,
  onLikeUpdate,
  onPostUpdated,
  onSaveUpdate,
  isGridView = true,
  className,
  defaultShowComments = false,
  commentAction = "toggle",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useUserAuth();
  const { showToast } = useToast();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLiked, setIsLiked] = useState(post.isLiked || false);
  const [likeCount, setLikeCount] = useState(
    post.likeCount || post.likes?.length || 0,
  );
  const [commentCount, setCommentCount] = useState(
    post.commentCount || post.comments?.length || 0,
  );
  const [showComments, setShowComments] = useState(defaultShowComments);
  const [hasLoadedComments, setHasLoadedComments] = useState(false);
  const [isSaved, setIsSaved] = useState(post.isSaved || false);
  const [saving, setSaving] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [backdropColor, setBackdropColor] = useState(null);
  const colorCache = React.useRef({});

  useEffect(() => {
    const trackView = async () => {
      try {
        await api.post(`/posts/${post._id}/view`);
      } catch (err) {
        console.debug("View tracking failed:", err);
      }
    };

    if (post._id) {
      trackView();
    }
  }, [post._id]);

  useEffect(() => {
    setCommentCount(post.commentCount || post.comments?.length || 0);
    setHasLoadedComments(false);
    setShowComments(defaultShowComments);
    setIsLiked(post.isLiked || false);
    setLikeCount(post.likeCount || post.likes?.length || 0);
    setCurrentImageIndex(0);
  }, [
    post._id,
    defaultShowComments,
    post.isLiked,
    post.likeCount,
    post.commentCount,
    post.comments?.length,
  ]);
  const [liking, setLiking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const productCount = post.products?.length || 0;
  const products = post.products || [];

  const getProductId = (product) => {
    if (!product) return null;
    if (typeof product === "string") return product;
    return product._id || product.id || null;
  };

  const hasProductImages =
    post.productImages && Object.keys(post.productImages).length > 0;
  const fallbackImages = post.images || [];

  const getImagesForProduct = (product) => {
    const productId = getProductId(product);
    if (
      hasProductImages &&
      productId &&
      Array.isArray(post.productImages[productId]) &&
      post.productImages[productId].length > 0
    ) {
      return post.productImages[productId];
    }
    return fallbackImages;
  };

  const productImageEntries = products.reduce((acc, product, index) => {
    const images = getImagesForProduct(product) || [];
    images.forEach((image, imageIndex) => {
      acc.push({
        productIndex: index,
        imageIndex,
        image,
        productId: getProductId(product),
      });
    });
    return acc;
  }, []);

  if (productImageEntries.length === 0 && fallbackImages.length > 0) {
    fallbackImages.forEach((image, imageIndex) => {
      productImageEntries.push({
        productIndex: 0,
        imageIndex,
        image,
        productId: getProductId(products[0]),
      });
    });
  }

  const safeImageIndex =
    productImageEntries.length > 0
      ? Math.min(currentImageIndex, productImageEntries.length - 1)
      : 0;
  const currentEntry = productImageEntries[safeImageIndex] || null;
  const currentProductIndex = currentEntry?.productIndex || 0;
  const currentProduct = products[currentProductIndex] || products[0] || null;
  const currentImages = currentProduct
    ? getImagesForProduct(currentProduct)
    : fallbackImages;
  const currentProductImageIndex = currentEntry?.imageIndex || 0;
  const currentImageUrl = productImageEntries[safeImageIndex]?.image
    ? getImageUrl(productImageEntries[safeImageIndex].image)
    : "";
  const containerBg = backdropColor
    ? `rgb(${backdropColor.r}, ${backdropColor.g}, ${backdropColor.b})`
    : undefined;
  const overlayColor = backdropColor
    ? `rgba(${backdropColor.r}, ${backdropColor.g}, ${backdropColor.b}, 0.9)`
    : "rgba(0, 0, 0, 0.7)";

  const isOwnPost =
    currentUser &&
    post.user &&
    (currentUser.id === post.user._id || currentUser._id === post.user._id);

  const handleLike = async () => {
    if (!currentUser) {
      navigate("/login", { state: { from: location, action: "like post" } });
      return;
    }

    try {
      setLiking(true);
      const res = await api.post(`/posts/${post._id}/like`);
      setIsLiked(res.data.isLiked);
      setLikeCount(res.data.likeCount);
      onLikeUpdate?.(post._id, res.data.isLiked, res.data.likeCount);
    } catch (err) {
      console.error("Error liking post:", err);
      showToast({
        message: err.response?.data?.message || "Failed to like post",
        type: "error",
      });
    } finally {
      setLiking(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser) {
      navigate("/login", { state: { from: location, action: "save post" } });
      return;
    }

    try {
      setSaving(true);
      if (isSaved) {
        await api.delete(`/posts/${post._id}/save`);
        setIsSaved(false);
        onSaveUpdate?.(post._id, false);
      } else {
        await api.post(`/posts/${post._id}/save`);
        setIsSaved(true);
        onSaveUpdate?.(post._id, true);
      }
    } catch (err) {
      console.error("Error saving post:", err);
      showToast({
        message: err.response?.data?.message || "Failed to save post",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleShare = (e) => {
    e?.stopPropagation?.();
    setShowShareModal(true);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    try {
      setDeleting(true);
      await api.delete(`/posts/${post._id}`);
      onDelete?.(post._id);
    } catch (err) {
      console.error("Error deleting post:", err);
      showToast({
        message: err.response?.data?.message || "Failed to delete post",
        type: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  const nextImage = () => {
    if (productImageEntries.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % productImageEntries.length);
    }
  };

  const prevImage = () => {
    if (productImageEntries.length > 1) {
      setCurrentImageIndex(
        (prev) =>
          (prev - 1 + productImageEntries.length) % productImageEntries.length,
      );
    }
  };

  const handleCardClick = (e) => {
    if (
      e.target.closest("button") ||
      e.target.closest("a") ||
      e.target.closest("svg")
    ) {
      return;
    }
    const fromPath = `${location.pathname}${location.search}`;
    navigate(`/post/${post._id}`, { state: { from: fromPath } });
  };

  const handleCommentClick = (e) => {
    e.stopPropagation();
    if (commentAction === "navigate") {
      const fromPath = `${location.pathname}${location.search}`;
      navigate(`/post/${post._id}`, { state: { from: fromPath } });
      return;
    }
    setShowComments((prev) => !prev);
  };

  const handleImageLoad = (e) => {
    const img = e.target;
    const imageUrl = img.currentSrc || img.src;
    if (!imageUrl) return;

    if (colorCache.current[imageUrl]) {
      setBackdropColor(colorCache.current[imageUrl]);
      return;
    }

    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Use a larger sample size to get better edge representation
      const sampleSize = 50;
      canvas.width = sampleSize;
      canvas.height = sampleSize;

      // Sample from edges instead of entire image
      ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
      const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
      const pixels = imageData.data;

      let r = 0,
        g = 0,
        b = 0,
        count = 0;

      // Sample only from the edges
      const borderThickness = 5; // Sample from outer 5 pixels

      for (let y = 0; y < sampleSize; y++) {
        for (let x = 0; x < sampleSize; x++) {
          // Only sample if we're on the border
          if (
            x < borderThickness ||
            x >= sampleSize - borderThickness ||
            y < borderThickness ||
            y >= sampleSize - borderThickness
          ) {
            const index = (y * sampleSize + x) * 4;
            const alpha = pixels[index + 3];
            if (alpha === 0) continue;

            r += pixels[index];
            g += pixels[index + 1];
            b += pixels[index + 2];
            count++;
          }
        }
      }

      if (count > 0) {
        const color = {
          r: Math.round(r / count),
          g: Math.round(g / count),
          b: Math.round(b / count),
        };
        colorCache.current[imageUrl] = color;
        setBackdropColor(color);
      }
    } catch (error) {
      console.debug("Color extraction failed", error);
    }
  };

  return (
    <div
      className={`${styles.postCard} ${
        isGridView ? styles.postCardGrid : styles.postCardFull
      } ${className || ""}`}
      onClick={handleCardClick}
    >
      <div className={styles.postHeader}>
        {post.user ? (
          <Link
            to={`/user/${post.user._id}`}
            className={styles.userInfo}
            onClick={(e) => e.stopPropagation()}
          >
            {post.user.avatar ? (
              <img
                src={getImageUrl(post.user.avatar)}
                alt={post.user?.firstName || "User"}
                className={styles.userAvatar}
              />
            ) : (
              <div className={styles.userAvatarPlaceholder}>
                {post.user?.firstName?.[0] || "U"}
              </div>
            )}
            <div className={styles.userDetails}>
              <div className={styles.userName}>
                {post.user?.firstName || ""} {post.user?.lastName || ""}
              </div>
              {post.user.username && (
                <div className={styles.userUsername}>@{post.user.username}</div>
              )}
            </div>
          </Link>
        ) : (
          <div className={styles.userInfo}>
            <div className={styles.userAvatarPlaceholder}>U</div>
            <div className={styles.userDetails}>
              <div className={styles.userName}>[Deleted User]</div>
            </div>
          </div>
        )}
        <div className={styles.postActions}>
          {isOwnPost ? (
            <>
              <button
                className={styles.editButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEditModal(true);
                }}
                title="Edit post"
              >
                <FaEdit />
              </button>
              <button
                className={styles.deleteButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick();
                }}
                disabled={deleting}
                title="Delete post"
              >
                <FaTimes />
              </button>
            </>
          ) : (
            <div className={styles.menuContainer}>
              <button
                className={styles.menuButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                }}
                title="More options"
              >
                <FaEllipsisV />
              </button>
              {showMenu && (
                <div className={styles.menuDropdown}>
                  <button
                    className={styles.menuItem}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      setShowReportModal(true);
                    }}
                  >
                    <FaFlag /> Report Post
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {productImageEntries.length > 0 && (
        <div
          className={styles.imageContainer}
          style={{ backgroundColor: containerBg }}
        >
          {currentImageUrl && (
            <div
              className={styles.imageBackdrop}
              style={{
                backgroundColor: containerBg,
              }}
            />
          )}
          <img
            src={currentImageUrl}
            alt={`Post by ${post.user?.firstName || "User"}`}
            className={styles.postImage}
            onLoad={handleImageLoad}
            crossOrigin="anonymous"
          />
          {productImageEntries.length > 1 && (
            <>
              <button
                className={`${styles.imageNavButton} ${styles.imageNavLeft}`}
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
              >
                <FaChevronLeft size={20} />
              </button>
              <button
                className={`${styles.imageNavButton} ${styles.imageNavRight}`}
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
              >
                <FaChevronRight size={20} />
              </button>
              <div className={styles.imageIndicator}>
                {`${currentProductImageIndex + 1} / ${
                  currentImages.length || 1
                }`}
              </div>
            </>
          )}
        </div>
      )}

      {post.products && post.products.length > 0 && (
        <div className={styles.productsSection}>
          <FaTag className={styles.productsIcon} />
          <div className={styles.productsList}>
            {products.map((product, index) => {
              const productId = getProductId(product);
              const productName =
                typeof product === "string"
                  ? "Product"
                  : product?.name || "Product";
              return (
                <div key={productId || index} className={styles.productTagItem}>
                  <Link
                    to={`/product/${productId}`}
                    className={`${styles.productTag} ${
                      index === currentProductIndex
                        ? styles.productTagActive
                        : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      const targetIndex = productImageEntries.findIndex(
                        (entry) => entry.productIndex === index,
                      );
                      setCurrentImageIndex(targetIndex >= 0 ? targetIndex : 0);
                    }}
                  >
                    {capitalizeFirst(productName)}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {post.caption && (
        <div className={`${styles.caption} ${styles.captionTruncated}`}>
          <span className={styles.captionText}>
            {renderMentionText(post.caption)}
          </span>
        </div>
      )}

      {post.tags && post.tags.length > 0 && (
        <div
          className={`${styles.tags} ${isGridView ? styles.tagsTruncated : ""}`}
        >
          {post.tags.map((tag, idx) => (
            <span key={idx} className={styles.tag}>
              7{tag}
            </span>
          ))}
        </div>
      )}

      <div className={styles.engagement}>
        <button
          className={`${styles.engagementButton} ${
            isLiked ? styles.liked : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleLike();
          }}
          disabled={liking}
        >
          {isLiked ? <FaHeart /> : <FaRegHeart />}
          <span>{likeCount}</span>
        </button>
        <button
          className={styles.engagementButton}
          onClick={handleCommentClick}
        >
          <FaComment />
          <span>{commentCount}</span>
        </button>
        <button
          className={styles.engagementButton}
          onClick={handleShare}
          title="Share post"
        >
          <FaShare />
          <span>Share</span>
        </button>
        <button
          className={`${styles.engagementButton} ${
            isSaved ? styles.saved : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleSave();
          }}
          disabled={saving}
          title={isSaved ? "Remove from saved" : "Save post"}
        >
          {isSaved ? <FaBookmark /> : <FaRegBookmark />}
          <span>{isSaved ? "Saved" : "Save"}</span>
        </button>
      </div>

      <div className={styles.timestamp}>
        {new Date(post.createdAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year:
            new Date(post.createdAt) >
            new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)
              ? undefined
              : "numeric",
        })}
      </div>

      {showComments && (
        <PostComments
          postId={post._id}
          postOwnerId={post.user?._id}
          onCommentAdded={(newComment) => {
            setCommentCount((prev) => prev + 1);
          }}
          onCommentDeleted={(deletedCount = 1) => {
            setCommentCount((prev) => Math.max(0, prev - deletedCount));
          }}
          onCommentsLoaded={(totalCount) => {
            setCommentCount(totalCount);
            setHasLoadedComments(true);
          }}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          message="Are you sure you want to delete this post?"
          onConfirm={handleDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}

      {showEditModal && (
        <EditPost
          post={post}
          onPostUpdated={(updatedPost) => {
            setShowEditModal(false);
            if (onPostUpdated) {
              onPostUpdated(updatedPost);
            } else {
              window.location.reload();
            }
          }}
          onCancel={() => setShowEditModal(false)}
        />
      )}

      {showReportModal && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          contentType="post"
          contentId={post._id}
          onReported={() => {
            setShowReportModal(false);
          }}
        />
      )}

      <SharePostModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        post={post}
      />
    </div>
  );
};

export default ProductDetailsPostCard;
